import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import {
  CreatedResponse,
  CustomerService,
  EmptySuccessfulResponse,
  GeneralApiResponse,
  GroupCreateRequest,
  GroupItemResponse,
  GroupListItemResponse,
  GroupUpdateRequest,
  ImportUploadResponse,
  PspService,
  UserItemResponse,
} from '../../../api';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BASE_SKIP_ERROR_TOAST } from '../../core/constants/base-http-context-tokens';

@Injectable({ providedIn: 'root' })
export class BaseUsersService {
  constructor(private pspService: PspService, private customerService: CustomerService) {}

  getUserList(params: {
    id: number;
    role?: string;
    isPSP: boolean;
    search?: string;
    active?: boolean;
  }): Observable<UserItemResponse[]> {
    const source$ = params.isPSP
      ? this.pspService.getPspsUsersList({
          psp: params.id,
          'role[]': params.role,
          search: params.search,
          active: params.active,
        })
      : this.customerService.getCustomersUsersList({
          customer: params.id,
          'role[]': params.role,
          search: params.search,
          active: params.active,
        });

    return source$.pipe(map((response) => (response.data?.length ? response.data : [])));
  }

  getPspUserGroups(psp: any): Observable<Array<GroupListItemResponse> | undefined> {
    return this.pspService.getPspGroupsList({ psp }).pipe(
      take(1),
      map((response: { message?: string; data?: Array<GroupListItemResponse> }) =>
        response.data?.length ? response.data : []
      )
    );
  }

  getCustomerUserGroups(customer: any): Observable<Array<GroupListItemResponse> | undefined> {
    return this.customerService.getCustomerGroupsList({ customer }).pipe(
      take(1),
      map((response: { message?: string; data?: Array<GroupListItemResponse> }) =>
        response.data?.length ? response.data : []
      )
    );
  }

  getPspGroup(groupId: number, pspId: number): Observable<GeneralApiResponse & { data?: GroupItemResponse }> {
    return this.pspService.getPspGroupsShow({ group: groupId, psp: pspId });
  }

  getCustomerGroup(groupId: number, customerId: number): Observable<GeneralApiResponse & { data?: GroupItemResponse }> {
    return this.customerService.getCustomerGroupsShow({ customer: customerId, group: groupId });
  }

  createPspGroup(pspId: number, body: GroupCreateRequest): Observable<CreatedResponse> {
    return this.pspService.postPspGroupCreate({ psp: pspId, body });
  }

  updatePspGroup(groupId: number, pspId: number, body: GroupUpdateRequest): Observable<EmptySuccessfulResponse> {
    return this.pspService.putPspGroupsUpdate({ group: groupId, psp: pspId, body });
  }

  createCustomerGroup(customerId: number, body: GroupCreateRequest): Observable<CreatedResponse> {
    return this.customerService.postCustomerGroupCreate({ customer: customerId, body });
  }

  updateCustomerGroup(
    groupId: number,
    customerId: number,
    body: GroupUpdateRequest
  ): Observable<EmptySuccessfulResponse> {
    return this.customerService.putCustomerGroupsUpdate({ group: groupId, customer: customerId, body });
  }

  deletePspUsersGroup(psp: number, group: number): Observable<EmptySuccessfulResponse> {
    return this.pspService.deletePspGroupsDelete({ psp, group });
  }

  deleteCustomerUsersGroup(customer: number, group: number): Observable<EmptySuccessfulResponse> {
    return this.customerService.deleteCustomerGroupsDelete({ customer, group });
  }

  deletePspUserInvitation(params: { psp: number; user: number }): Observable<EmptySuccessfulResponse> {
    return this.pspService.deletePspsUserInviteDelete(params);
  }

  deleteCustomerUserInvitation(params: { customer: number; user: number }): Observable<EmptySuccessfulResponse> {
    return this.customerService.deleteCustomersUserInviteDelete(params);
  }

  resendPspUserInvitation(params: { psp: number; user: number }): Observable<CreatedResponse> {
    return this.pspService.postPspsUsersReinvitation(params);
  }

  resendCustomerUserInvitation(params: { customer: number; user: number }): Observable<CreatedResponse> {
    return this.customerService.postCustomersUserReinvitation(params);
  }

  activatePSPUser(params: { user: number; psp: number }): Observable<EmptySuccessfulResponse> {
    return this.pspService.postPspsUsersActivate(params);
  }

  deactivatePSPUser(params: { user: number; psp: number }): Observable<EmptySuccessfulResponse> {
    return this.pspService.deletePspsUsersDeactivate(params);
  }

  activateCustomerUser(params: { user: number; customer: number }): Observable<EmptySuccessfulResponse> {
    return this.customerService.postCustomersUsersActivate(params);
  }

  deactivateCustomerUser(params: { user: number; customer: number }): Observable<EmptySuccessfulResponse> {
    return this.customerService.deleteCustomersUsersDeactivate(params);
  }

  getUserImportTemplate(psp: number): Observable<Blob> {
    return this.pspService.getPspUserImportTemplate({ psp });
  }

  uploadUserImport(psp: number, file: File): Observable<ImportUploadResponse | undefined> {
    return this.pspService
      .postPspUserImportUpload({
        psp,
        body: { file },
        context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
      })
      .pipe(map((response) => response.data));
  }

  processUserImport(psp: number, importId: string): Observable<void> {
    return this.pspService.postPspUserImportProcess({ psp, import: importId });
  }

  getCustomerUserImportTemplate(customer: number): Observable<Blob> {
    return this.customerService.getCustomerUserImportTemplate({ customer });
  }

  uploadCustomerUserImport(customer: number, file: File): Observable<ImportUploadResponse | undefined> {
    return this.customerService
      .postCustomerUserImportUpload({
        customer,
        body: { file },
        context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
      })
      .pipe(map((response) => response.data));
  }

  processCustomerUserImport(customer: number, importId: string): Observable<void> {
    return this.customerService.postCustomerUserImportProcess({ customer, import: importId });
  }
}
